import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

import { translate } from '@jsverse/transloco';

import { AppSharedModule } from 'app/app-shared.module';
import { Constants } from 'app/constants/constants';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { ChartDisplayOptions } from '../../../../models';
import { NumericMetadataFieldSelectorComponent } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.component';
import { NumericMetadataField } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.models';

@EnableClassTelemetry()
@Component({
  selector: 'app-grid-chart-options-dialog',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AppSharedModule, NumericMetadataFieldSelectorComponent],
  templateUrl: './grid-chart-options-dialog.component.html',
  styleUrls: ['./grid-chart-options-dialog.component.scss'],
})
export class GridChartOptionsDialogComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() public data: ChartDisplayOptions;
  @Input() public isReadOnly = false;

  @Output() modalClosed: EventEmitter<ChartDisplayOptions> =
    new EventEmitter<ChartDisplayOptions>();

  isValid = true;
  public formGroup: FormGroup;
  fb = inject(FormBuilder);

  get isTableTransposed(): boolean {
    return this.formGroup.getRawValue().transpose;
  }

  get isComparisonActive(): boolean {
    return !!this.data.comparison;
  }

  get isDeltaDisabled(): boolean {
    return !this.isComparisonActive || this.data.comparison?.filters?.length !== 2;
  }

  get deltaTooltip(): string | null {
    if (!this.isComparisonActive) {
      return translate('widgets.options.show-delta-column-disabled-no-comparison');
    }
    if (this.data.comparison?.filters?.length !== 2) {
      return translate('widgets.options.show-delta-column-disabled-filter-count');
    }
    return null;
  }

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data?.currentValue && this.formGroup) {
      if (this.isComparisonActive) {
        this.formGroup.controls.transpose.disable();
      } else {
        this.formGroup.controls.transpose.enable();
        if (this.formGroup.getRawValue().transpose !== this.data.payload.transpose) {
          this.formGroup.controls.transpose.patchValue(this.data.payload.transpose);
        }
      }
      if (this.isDeltaDisabled) {
        this.formGroup.controls.showDeltaColumn.disable();
      } else {
        this.formGroup.controls.showDeltaColumn.enable();
      }
    }
  }

  private buildForm() {
    this.formGroup = this.fb.group<{ transpose: FormControl; showDeltaColumn: FormControl }>({
      transpose: this.fb.control({
        value: this.data.payload.transpose || false,
        disabled: this.isComparisonActive,
      }),
      showDeltaColumn: this.fb.control({
        value: this.data.payload.showDeltaColumn ?? true,
        disabled: this.isDeltaDisabled,
      }),
    });
  }

  onSave(): void {
    const result = { ...this.data };
    const originalTranspose = this.data.payload.transpose;
    const newTranspose = this.isTableTransposed;

    result.payload.transpose = newTranspose;
    result.payload.showDeltaColumn = this.formGroup.getRawValue().showDeltaColumn;

    // transpose option actually changed only then we reset sorting.
    if (originalTranspose !== newTranspose) {
      result.payload.sort.field = newTranspose ? Constants.TRANSPOSED_METADATA_COLUMN : 'breakdown';
    }

    result.needRefresh = true;

    this.modalClosed.emit(result);
  }

  onCancel(): void {
    this.modalClosed.emit();
  }

  onNumericMetadataFieldsChanged(fields: NumericMetadataField[]): void {
    this.data.payload.numericMetadataFields = fields;
  }

  onNumericMetadataFieldsIsValid(isValid: boolean): void {
    this.isValid = isValid;
  }
}
